/**
 * Deep links into the app's working screens.
 *
 * The dashboard exists to get work done, not only to look at numbers, so its
 * buttons land on the screen with the right panel already open. A link is an
 * ordinary URL with a small, documented set of parameters; the target page reads
 * each one once and then strips it (see `useDeepLink`), so the address bar stays
 * clean and a reload does not reopen a form the person already closed.
 *
 * Kept as pure string builders so they can be unit-tested and used anywhere.
 */

export const DEEP_LINK_PARAMS = {
  action: 'action',
  lot: 'lot',
  batch: 'batch',
  item: 'item',
  tab: 'tab',
  search: 'search',
} as const;

/** The stock operations the inventory screen can be asked to open. */
export type StockActionIntent = 'issue' | 'transfer' | 'adjust' | 'scrap';

type Params = Record<string, string | undefined>;

/** Append only the parameters that are actually set. */
export function link(path: string, params: Params = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, value);
  }
  const serialised = search.toString();
  return serialised ? `${path}?${serialised}` : path;
}

export const links = {
  /** Receive stock; `itemId` opens the form with that material already chosen. */
  inventoryReceive: (itemId?: string) => link('/inventory', { action: 'receive', item: itemId }),
  /** Ask inventory to open one movement type once a lot has been picked. */
  inventoryAction: (action: StockActionIntent) => link('/inventory', { action }),
  inventoryLedger: () => link('/inventory', { tab: 'ledger' }),
  inventoryBalances: () => link('/inventory', { tab: 'balances' }),
  inventoryLot: (lotId: string) => link('/inventory', { lot: lotId }),
  inventorySearch: (term: string) => link('/inventory', { search: term }),
  batch: (batchId: string) => link('/batches', { batch: batchId }),
  newBatch: () => link('/batches', { action: 'new' }),
  newRecovery: (batchId?: string) => link('/recovery', { action: 'new', batch: batchId }),
  qualityLot: (lotId: string) => link('/quality', { lot: lotId }),
} as const;
